import React, { createContext, FC, ReactNode, useContext, useMemo } from 'react';

import { FolderType, OrganizationStructureDTO, useGetOrganizationStructure } from '@/api/generated';
import {
  buildFlatStructure,
  buildMapStructure,
  buildRecursiveStructure
} from '@/components/fields/Location/locationTreeUtils';
import { RecursiveItem, RecursiveOrganizationStructure, SimpleItem } from '@/components/fields/Location/types';

interface OrganizationStructureContextValue {
  organizationStructure: OrganizationStructureDTO | undefined;
  recursiveStructure: RecursiveOrganizationStructure | undefined;
  flatStructure: SimpleItem[];
  mapStructure: { [key: string]: RecursiveItem };
  isLoading: boolean;
  location?: RecursiveItem;
}

const OrganizationStructureContext = createContext<OrganizationStructureContextValue>({
  organizationStructure: undefined,
  recursiveStructure: undefined,
  flatStructure: [],
  mapStructure: {},
  isLoading: false
});

interface OrganizationStructureProviderProps {
  children: ReactNode;
  organizationSlug?: string;
  allowedTypes?: FolderType[];
}

export const OrganizationStructureProvider: FC<OrganizationStructureProviderProps> = ({
  children,
  organizationSlug
}) => {
  const { data: organizationStructure, isLoading } = useGetOrganizationStructure(organizationSlug!, {
    query: {
      enabled: !!organizationSlug
    }
  });

  const recursiveStructure = useMemo(
    () => organizationStructure && buildRecursiveStructure(organizationStructure),
    [organizationStructure]
  );

  const flatStructure = useMemo(
    () => (organizationStructure ? buildFlatStructure(organizationStructure) : []),
    [organizationStructure]
  );

  const mapStructure = useMemo(
    () => (recursiveStructure ? buildMapStructure(recursiveStructure) : {}),
    [recursiveStructure]
  );

  const value = useMemo(
    () => ({
      organizationStructure,
      recursiveStructure,
      flatStructure,
      mapStructure,
      isLoading
    }),
    [organizationStructure, recursiveStructure, flatStructure, mapStructure, isLoading]
  );

  return <OrganizationStructureContext.Provider value={value}>{children}</OrganizationStructureContext.Provider>;
};

export const useOrganizationStructure = (locationSlug?: string): OrganizationStructureContextValue => {
  const originalContext = useContext(OrganizationStructureContext);

  if (!originalContext) {
    throw new Error('useOrganizationStructure must be used within an OrganizationStructureProvider');
  }

  const location = useMemo(
    () => (locationSlug ? originalContext?.mapStructure?.[locationSlug] : null),
    [originalContext?.mapStructure, locationSlug]
  );

  const context = useMemo(
    () =>
      location
        ? {
            ...originalContext,
            location
          }
        : originalContext,
    [originalContext, location]
  );

  return context;
};
